import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  IconButton,
  Popover,
  MuiList as List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Paper,
  Alert,
} from "components/mui";
import {
  Add as AddIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  ContentPaste as PasteIcon,
} from "@mui/icons-material";

import {
  AVAILABLE_VARIABLES,
  getUsedVariables,
  validateVariables,
} from "libs/utils/canmailVariables";

function VariableToolbar({
  onVariableInsert,
  content,
  showInsertButton = true,
  isDropEditor = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  // Get unique variables used in content
  const usedVariables = useMemo(() => getUsedVariables(content), [content]);

  // Flag typo'd or malformed variables. The backend only substitutes the three
  // known tokens, so anything else is delivered to recipients as literal text.
  // `validateVariables` reports one error per occurrence; collapse repeats of the
  // same typo so a variable used five times is listed once.
  const variableErrors = useMemo(() => {
    const seen = new Map();
    validateVariables(content).errors.forEach((error) => {
      // An unclosed `[[` can swallow the rest of the document into `variable`,
      // so truncate before using it as a key.
      const key = `${error.type}-${String(error.variable).slice(0, 64)}`;
      if (!seen.has(key)) seen.set(key, { ...error, key });
    });
    return [...seen.values()];
  }, [content]);

  const handleVariableClick = (variable) => {
    onVariableInsert(`[[${variable.key}]]`);
    setAnchorEl(null);
  };

  const handleInfoClick = () => {
    setShowInfo(!showInfo);
  };

  const open = Boolean(anchorEl);
  const id = open ? "variable-popover" : undefined;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Single row: Insert Icon + Variables: + Chips + Info Icon */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Insert icon on the left */}
        {showInsertButton && (
          <Tooltip
            title={
              isDropEditor ? "Copy variable to clipboard" : "Insert variable"
            }
          >
            <IconButton
              size="small"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              aria-describedby={id}
            >
              {isDropEditor ? (
                <PasteIcon fontSize="small" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Variables label */}
        <Typography variant="body2" color="text.secondary">
          Variables:
        </Typography>

        {/* Always visible chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {AVAILABLE_VARIABLES.map((variable) => {
            const isUsed = usedVariables.includes(variable.key);
            return (
              <Chip
                key={variable.key}
                icon={<CodeIcon />}
                label={variable.label}
                size="small"
                color={isUsed ? "primary" : "default"}
                variant="outlined"
                onClick={() => handleVariableClick(variable)}
                sx={{
                  cursor: "pointer",
                  opacity: isUsed ? 1 : 0.6,
                  "&:hover": {
                    opacity: 1,
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Info icon after chips */}
        <Tooltip
          title={
            isDropEditor
              ? "Variable usage help for visual editor"
              : "Variable help and examples"
          }
        >
          <IconButton size="small" onClick={handleInfoClick}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Warn about typo'd / malformed variables. Non-blocking: a user may
          legitimately want literal double brackets in their copy. */}
      {variableErrors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {variableErrors.length === 1
              ? "1 variable will not be replaced:"
              : `${variableErrors.length} variables will not be replaced:`}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {variableErrors.map((error) => (
              <li key={error.key}>
                <Typography variant="body2" component="span">
                  {error.message}
                </Typography>
              </li>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            These will be sent to recipients exactly as written.
          </Typography>
        </Alert>
      )}

      {/* Variable insertion popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { maxHeight: 400, width: 300 },
        }}
      >
        <Paper sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ p: 1, fontWeight: "bold" }}>
            {isDropEditor ? "Copy Variable to Clipboard" : "Insert Variable"}
          </Typography>
          <Divider />
          <List dense>
            {AVAILABLE_VARIABLES.map((variable) => (
              <ListItem key={variable.key} disablePadding>
                <ListItemButton onClick={() => handleVariableClick(variable)}>
                  <ListItemText
                    primary={variable.label}
                    secondary={`[[${variable.key}]] - ${variable.description}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>

      {/* Info popover */}
      <Popover
        open={showInfo}
        anchorEl={document.body}
        onClose={() => setShowInfo(false)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          sx: { maxWidth: 500, p: 2 },
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Using Variables in CANmail
          </Typography>

          <Typography variant="body2" paragraph>
            Variables allow you to personalize emails for each contact. They
            will be automatically replaced with actual contact data when emails
            are sent.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Available Variables:
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 2,
            }}
          >
            {AVAILABLE_VARIABLES.map((variable) => (
              <Box
                key={variable.key}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {variable.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`[[${variable.key}]]`} → {variable.example}
                </Typography>
              </Box>
            ))}
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Variables will be automatically replaced
              with actual contact data when emails are sent.
            </Typography>
          </Alert>

          {isDropEditor && (
            <Typography variant="body2">
              Variables are copied to your clipboard.
              Paste them into any text area in your email design.
            </Typography>
          )}

        </Box>
      </Popover>
    </Box>
  );
}

export default VariableToolbar;
